import {
  Accordion,
  Badge,
  Checkbox,
  SelectBox,
  Switch,
  TextField,
  Typography,
} from "@/components/kit";
import { useMemo, useState } from "react";
import classNames from "classnames";
import { Field, FieldProps, useFormikContext } from "formik";
import { EMspAdminRole, IUser } from "../../types";
import { useGetRoles } from "@/hooks/query/acl/useGetRoles";
import useStoreMe from "@/hooks/store/useStoreMe";

const SecurityAndAccess = ({
  forItAdmin = false,
  userId,
}: {
  forItAdmin?: boolean;
  userId?: string;
  isEdit?: boolean;
}) => {
  const [isOpenSecurityAndAccess, setIsOpenSecurityAndAccess] = useState<
    number | undefined
  >(0);
  const [isOpenAccess, setIsOpenAccess] = useState<number | undefined>(0);
  const [isSelectBoxFocused, setIsSelectBoxFocused] = useState(false);
  const [isMfaSelectBoxFocused, setIsMfaSelectBoxFocused] = useState(false);
  const { values, setFieldValue } = useFormikContext<IUser>();
  const { me } = useStoreMe();
  const isMe = me?.id === userId;
  const { data: roles } = useGetRoles();
  const roleOptions = useMemo(() => {
    return roles?.map((role) => ({
      value: role.id,
      label: EMspAdminRole[role.name as keyof typeof EMspAdminRole],
    }));
  }, [roles]);

  return (
    <div className="flex flex-col gap-5">
      {!forItAdmin && (
        <Accordion
          title={
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-2">
                <Typography variant="Title5-Regular">🛡️Access </Typography>
                <Badge size="sm" state="danger">
                  Required
                </Badge>
              </div>

              {isOpenAccess === 0 && (
                <Typography color="text-gray-300" variant="Body3-Regular">
                  Assign an access role to define this admin’s permissions
                  across MSP features and billing.
                </Typography>
              )}
            </div>
          }
          isOpen={isOpenAccess === 0}
          onChange={() => {
            setIsOpenAccess((p) => (p === 0 ? undefined : 0));
          }}
          descriptionClassName={classNames("!mt-0 !px-6", {
            "!py-0": isOpenAccess !== 0,
            "!pb-4": isOpenAccess === 0,
          })}
          headerClassName={classNames("!px-6 !py-5")}
          containerClassName={classNames({
            "!z-[100]": isSelectBoxFocused && isOpenAccess === 0,
          })}
        >
          <div className="w-full">
            <Field name="roleId">
              {({ field }: FieldProps) => (
                <SelectBox
                  {...field}
                  disabled={isMe}
                  label="select one role"
                  onChange={(e) => setFieldValue("roleId", e)}
                  value={field.value}
                  dropdownZIndex={10000}
                  options={
                    roleOptions?.filter(
                      (item) =>
                        !(
                          !isMe
                            ? [EMspAdminRole.OWNER, EMspAdminRole.IT_ADMIN]
                            : []
                        ).includes(item.label as EMspAdminRole),
                    ) ?? []
                  }
                  renderOption={(item) => (
                    <Typography variant="Body1-Regular">
                      {item.label}
                    </Typography>
                  )}
                  renderValue={(item) => (
                    <Typography variant="Body1-Regular">
                      {item.label}
                    </Typography>
                  )}
                  containerClassName="z-50"
                  onFocus={() => setIsSelectBoxFocused(true)}
                  onClose={() => setIsSelectBoxFocused(false)}
                />
              )}
            </Field>
          </div>
        </Accordion>
      )}

      <Accordion
        title={
          <div className="flex flex-col gap-1 ">
            <div className="flex items-center gap-2">
              <Typography variant="Title5-Regular">
                🔐 Admin security settings{" "}
              </Typography>
            </div>

            {isOpenSecurityAndAccess === 0 && (
              <Typography color="text-gray-300" variant="Body3-Regular">
                Set up essential security options to protect user accounts and
                enforce organizational compliance.
              </Typography>
            )}
          </div>
        }
        isOpen={isOpenSecurityAndAccess === 0}
        onChange={() => {
          setIsOpenSecurityAndAccess((p) => (p === 0 ? undefined : 0));
        }}
        descriptionClassName={classNames("!mt-0 !px-6", {
          "!py-0": isOpenSecurityAndAccess !== 0,
          "!pb-4": isOpenSecurityAndAccess === 0,
        })}
        headerClassName={classNames("!px-6 !py-5 ")}
        containerClassName={classNames({
          "!z-[9999]": isMfaSelectBoxFocused && isOpenSecurityAndAccess === 0,
        })}
      >
        <div className="flex flex-col gap-6">
          <div className="flex flex-col gap-2">
            <div className="flex justify-between w-full">
              <div className="flex flex-col gap-1 w-full">
                <Typography variant="Body1-Medium">MFA enforced</Typography>
                <Typography color="text-gray-300" variant="Caption1-Regular">
                  Configure MFA, password policies, and user role access in one
                  place.
                </Typography>
              </div>
              <Field name="mfaEnforced">
                {({ field, meta: { error } }: FieldProps) => (
                  <Switch
                    {...field}
                    error={error}
                    checked={field.value}
                    onChange={(e) => field.onChange(e)}
                  />
                )}
              </Field>
            </div>
            <div className="flex  justify-between items-center w-full">
              <div className="flex items-center gap-2 !px-3">
                <Typography variant="Body2-Regular" className="!text-gray-700 ">
                  How long the user has to complete MFA configuration after
                  their first login?
                </Typography>
              </div>

              <div className="w-[100px] h-8">
                <Field name="mfaEnrollmentGracePeriod">
                  {({ field }: FieldProps) => (
                    // <TextField
                    //   type="number"
                    //   {...field}
                    //   error={touched && error}
                    //   placeholder="Days"
                    //   className="!w-[100px] !h-8"
                    //   endAdornment={"days"}
                    // />

                    <TextField
                      disabled={!values.mfaEnforced}
                      type="number"
                      placeholder="days"
                      {...field}
                      className={`!h-[32px] !w-[100px]`}
                      onChange={(e) =>
                        setFieldValue(field.name, +e.target.value)
                      }
                    />
                  )}
                </Field>
              </div>
            </div>
          </div>
          <div>
            <div className="flex justify-between w-full">
              <div className="flex flex-col gap-1 w-full">
                <Typography variant="Body1-Medium">
                  Auto-generated a temporary password
                </Typography>
                <Typography color="text-gray-300" variant="Caption1-Regular">
                  With this option you can let users to use a temporary password
                  for login. note that this expires in 48 hours.
                </Typography>
              </div>
              <Field name="temporaryPassword">
                {({ field, meta: { error } }: FieldProps) => (
                  <Switch
                    {...field}
                    error={error}
                    checked={field.value}
                    onChange={(e) => field.onChange(e)}
                  />
                )}
              </Field>
            </div>
            <div
              className="flex  items-center gap-2 mt-2 ps-3
             w-full"
            >
              <Checkbox
                checked={values.forcedToChangePassword}
                id="forcedToChangePassword"
                onChange={(e) =>
                  setFieldValue("forcedToChangePassword", e.target.checked)
                }
              />
              <Typography variant="Body2-Regular" className="">
                Forces the user to set a new password after signing in with the
                temporary one.
              </Typography>
            </div>
          </div>
          <div className="flex justify-between items-center w-full ">
            <Typography variant="Body1-Medium">MFA allowed method</Typography>
            <Field name="mfaAllowedType">
              {({ field, meta: { error } }: FieldProps) => (
                <SelectBox
                  {...field}
                  onChange={(e) => setFieldValue("mfaAllowedType", e)}
                  error={error}
                  options={[
                    { value: "email", label: "Email OTP" },
                    { value: "webauthn", label: "Authenticator" },
                  ]}
                  renderOption={(enumItem) => (
                    <Typography variant="Body3-Regular">
                      {enumItem.label}
                    </Typography>
                  )}
                  renderValue={(enumItem) => (
                    <Typography variant="Body3-Regular">
                      {enumItem.label}
                    </Typography>
                  )}
                  fixedDropdown
                  dropdownZIndex={10000}
                  className="!w-[300px] !h-11"
                  arrowClassName="border-l border-l-gray-200 "
                  onFocus={() => setIsMfaSelectBoxFocused(true)}
                  onClose={() => setIsMfaSelectBoxFocused(false)}
                />
              )}
            </Field>
          </div>
        </div>
      </Accordion>
    </div>
  );
};

export default SecurityAndAccess;
